import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Menu, X, User, LogOut, Sparkles, AlertTriangle } from 'lucide-react';

export function Navbar() {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout, isImpersonating, stopImpersonating } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleStopImpersonating = async () => {
    await stopImpersonating();
    navigate('/admin');
  };

  return (
    <>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-orange-500 text-white px-4 py-2 text-center text-sm">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            <AlertTriangle className="h-4 w-4" />
            <span>Estás actuando como <strong>{user?.name}</strong> ({user?.email})</span>
            <button 
              onClick={handleStopImpersonating}
              className="ml-4 bg-white text-orange-600 px-3 py-1 rounded text-xs font-bold hover:bg-orange-100 transition-colors"
            >
              Volver a Admin
            </button>
          </div>
        </div>
      )}
      
      <nav className="sticky top-0 z-50 glass border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Sparkles className="h-8 w-8 text-gold" style={{ color: '#D4A574' }} />
              <span className="font-serif text-2xl font-bold text-primary">Tantra Map</span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/sesiones" className="text-secondary hover:text-gold transition-colors" data-testid="nav-services">{t('nav.services')}</Link>
            <Link to="/formacion-y-eventos" className="text-secondary hover:text-gold transition-colors" data-testid="nav-courses">{t('nav.courses')}</Link>
            <Link to="/profesionales" className="text-secondary hover:text-gold transition-colors" data-testid="nav-teachers">{t('nav.teachers')}</Link>
            <Link to="/escuelas" className="text-secondary hover:text-gold transition-colors" data-testid="nav-schools">{t('nav.schools')}</Link>
            
            {/* Language Switcher */}
            <LanguageSwitcher variant="minimal" />

            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                {(user?.role === 'admin' || user?.roles?.includes('admin')) && (
                  <Link to="/admin" className="text-gold font-medium" data-testid="nav-admin">{t('nav.admin')}</Link>
                )}
                <Link to="/profile" className="flex items-center space-x-2 text-secondary hover:text-gold" data-testid="nav-profile">
                  <User className="h-5 w-5" />
                  <span>{user?.name}</span>
                </Link>
                <button onClick={handleLogout} className="text-secondary hover:text-gold" data-testid="logout-btn">
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="text-secondary">{t('nav.login')}</Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="bg-gold hover:bg-gold/90 text-white" style={{ backgroundColor: '#D4A574' }}>{t('nav.register')}</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button + Language Switcher */}
          <div className="flex md:hidden items-center gap-3">
            {/* Language Switcher - visible outside menu */}
            <LanguageSwitcher variant="minimal" />
            
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-primary p-2" data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-4 space-y-3">
            <Link to="/sesiones" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.services')}</Link>
            <Link to="/formacion-y-eventos" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.courses')}</Link>
            <Link to="/profesionales" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.teachers')}</Link>
            <Link to="/escuelas" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.schools')}</Link>
            
            {isAuthenticated ? (
              <>
                {(user?.role === 'admin' || user?.roles?.includes('admin')) && <Link to="/admin" className="block py-2 text-gold font-medium" onClick={() => setMobileMenuOpen(false)}>{t('nav.admin')}</Link>}
                <Link to="/profile" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.profile')}</Link>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block py-2 text-secondary">{t('nav.logout')}</button>
              </>
            ) : (
              <>
                <Link to="/login" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.login')}</Link>
                <Link to="/register" className="block py-2 text-gold font-medium" onClick={() => setMobileMenuOpen(false)}>{t('nav.register')}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
    </>
  );
}
